import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type {
  ExtractionJobStatus,
  LatestExtractionJob,
  ResourceScriptExtraction,
} from "../../../types/api/getResources";
import { triggerResourceExtraction } from "../../../services/resources/resources.service";
import { useNotification } from "../../../hooks/useNotification";

const POLLING_INTERVAL_MS = 5000;

const { Title, Text, Paragraph } = Typography;

interface ScriptExtractionTabProps {
  resourceId: number;
  scriptExtraction: ResourceScriptExtraction | null | undefined;
  job: LatestExtractionJob | null | undefined;
  scriptDocUrl: string | null | undefined;
  onRefetch: () => Promise<void> | void;
}

const isJobActive = (s: ExtractionJobStatus | undefined) =>
  s === "PENDING" || s === "IN_PROGRESS";

const STATUS_COPY: Record<ExtractionJobStatus, { label: string }> = {
  PENDING: { label: "En cola" },
  IN_PROGRESS: { label: "Procesando script" },
  COMPLETED: { label: "Completado" },
  FAILED: { label: "Fallido" },
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function ScriptExtractionTab({
  resourceId,
  scriptExtraction,
  job,
  scriptDocUrl,
  onRefetch,
}: ScriptExtractionTabProps) {
  const { notify } = useNotification();
  const [triggering, setTriggering] = useState(false);

  const onRefetchRef = useRef(onRefetch);
  useEffect(() => {
    onRefetchRef.current = onRefetch;
  }, [onRefetch]);

  const active = isJobActive(job?.status);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      void onRefetchRef.current?.();
    }, POLLING_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [active]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const result = await triggerResourceExtraction(resourceId);
      if (result.ok) {
        notify(
          "success",
          `Extracción de script encolada (job #${result.data.job_id}).`,
          "Extracción encolada",
        );
        await onRefetchRef.current?.();
        return;
      }
      if (result.kind === "conflict") {
        notify(
          "warning",
          `Ya hay una extracción en curso (job #${result.active_job.id} · ${result.active_job.status}).`,
          "En curso",
        );
        await onRefetchRef.current?.();
        return;
      }
      const title =
        result.kind === "not_found"
          ? "Recurso no encontrado"
          : result.kind === "bad_request"
            ? "No se puede extraer"
            : "Error";
      notify("error", result.message, title);
    } finally {
      setTriggering(false);
    }
  };

  const renderStatusBanner = () => {
    if (job) {
      const copy = STATUS_COPY[job.status];
      if (job.status === "PENDING" || job.status === "IN_PROGRESS") {
        return (
          <Alert
            type="info"
            showIcon
            icon={<Spin size="small" />}
            message={`${copy.label}…`}
            description={`Intento ${job.attempt_count} · actualizado ${formatDate(job.updated_at)}`}
            style={{ marginBottom: 16 }}
          />
        );
      }
      if (job.status === "FAILED") {
        return (
          <Alert
            type="error"
            showIcon
            message="La extracción falló"
            description={
              <>
                {job.error_message && (
                  <Paragraph style={{ margin: 0 }}>{job.error_message}</Paragraph>
                )}
                <Text type="secondary">
                  Intentos: {job.attempt_count} · {formatDate(job.updated_at)}
                </Text>
              </>
            }
            style={{ marginBottom: 16 }}
          />
        );
      }
      if (job.status === "COMPLETED") {
        return (
          <Alert
            type="success"
            showIcon
            message={`Extracción completada (${job.attempt_count} ${job.attempt_count === 1 ? "intento" : "intentos"})`}
            description={`Última actualización: ${formatDate(job.updated_at)}`}
            style={{ marginBottom: 16 }}
          />
        );
      }
    }

    if (scriptDocUrl) {
      return (
        <Alert
          type="info"
          showIcon
          message="Sin extracción"
          description="No se ha encolado un job de extracción para este recurso."
          style={{ marginBottom: 16 }}
        />
      );
    }

    return null;
  };

  const renderContent = () => {
    if (scriptExtraction) {
      return (
        <>
          <Space size="middle" wrap style={{ marginBottom: 12 }}>
            {scriptExtraction.model && (
              <Tag color="geekblue">Modelo: {scriptExtraction.model}</Tag>
            )}
            {scriptExtraction.extracted_at && (
              <Tag>Extraído: {formatDate(scriptExtraction.extracted_at)}</Tag>
            )}
            {scriptExtraction.image_warnings > 0 && (
              <Tag color="orange">
                {scriptExtraction.image_warnings === 1
                  ? "1 imagen con advertencia"
                  : `${scriptExtraction.image_warnings} imágenes con advertencias`}
              </Tag>
            )}
          </Space>

          {scriptExtraction.error_message && job?.status === "FAILED" && (
            <Alert
              type="warning"
              showIcon
              message="Detalle del error de extracción"
              description={scriptExtraction.error_message}
              style={{ marginBottom: 12 }}
            />
          )}

          {scriptExtraction.extracted_content ? (
            <Typography.Paragraph
              ellipsis={{ rows: 8, expandable: true, symbol: "Ver más" }}
              style={{
                background: "var(--bo-inset-bg)",
                border: "1px solid var(--bo-card-border)",
                borderRadius: 6,
                padding: 12,
                whiteSpace: "pre-wrap",
              }}
            >
              {scriptExtraction.extracted_content}
            </Typography.Paragraph>
          ) : (
            <Empty description="Aún no hay contenido extraído." />
          )}
        </>
      );
    }

    if (scriptDocUrl && !job) {
      return (
        <Empty description="Sin extracción aún. Tocá 'Re-extraer contenido' para iniciar." />
      );
    }

    return null;
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Extracción de guion
        </Title>
        <Popconfirm
          title="Re-extraer contenido"
          description="Se volverá a leer el documento de Google y se reemplazará el contenido extraído anterior. ¿Continuar?"
          okText="Sí, re-extraer"
          cancelText="Cancelar"
          onConfirm={handleTrigger}
          disabled={active || !scriptDocUrl}
        >
          <Tooltip
            title={
              active
                ? "Hay una extracción en curso. Esperá a que termine."
                : undefined
            }
          >
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              disabled={active || !scriptDocUrl}
              loading={triggering}
            >
              Re-extraer contenido
            </Button>
          </Tooltip>
        </Popconfirm>
      </div>

      {!scriptDocUrl ? (
        <Empty description="Pegá un URL de Google Docs en la pestaña Datos y guardá para iniciar la extracción." />
      ) : (
        <>
          {renderStatusBanner()}
          {renderContent()}
        </>
      )}
    </>
  );
}
